import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useOrg } from "@/lib/orgContext";
import { FileSignature, Loader2 } from "lucide-react";

type Method = "external_only" | "in_app_only" | "both";

const LABEL: Record<Method, string> = {
  external_only: "External e-sign provider only",
  in_app_only: "In-app signature only (Draw or Type)",
  both: "Both (signers can choose)",
};

export function SigningMethodsCard({ isAdmin }: { isAdmin: boolean }) {
  const { getToken } = useAuth();
  const { org, refetch } = useOrg();
  const { toast } = useToast();
  const [value, setValue] = useState<Method>("both");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (org?.allowedSigningMethods) setValue(org.allowedSigningMethods);
  }, [org?.allowedSigningMethods]);

  async function handleSave() {
    if (!org) return;
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/auth/org", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          institutionName: org.institutionName ?? org.name,
          division: org.division ?? "",
          conference: org.conference ?? "",
          allowedSigningMethods: value,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Save failed");
      await refetch();
      toast({ title: "Signing methods updated", description: LABEL[value] });
    } catch (err) {
      toast({
        title: "Update failed",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="h-5 w-5" />
          Signature Methods
        </CardTitle>
        <CardDescription>
          Control which signature methods signers may use during the Signature stage.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 max-w-md">
          <Label htmlFor="signing-methods">Allowed signing methods</Label>
          <Select value={value} onValueChange={(v) => setValue(v as Method)} disabled={!isAdmin}>
            <SelectTrigger id="signing-methods">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">{LABEL.both}</SelectItem>
              <SelectItem value="in_app_only">{LABEL.in_app_only}</SelectItem>
              <SelectItem value="external_only">{LABEL.external_only}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Default is <strong>Both</strong>. In-app signatures capture name, email, IP, user agent, timestamp, and a PNG of the signature.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={handleSave} disabled={saving || value === org?.allowedSigningMethods} size="sm">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
